import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { TrendingUp, Star, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface HeroProps {
  onAuthClick?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onAuthClick }) => {
  const [currentColor, setCurrentColor] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleBinderCardClick = () => {
    if (user) {
      navigate("/virtual-binder");
    } else {
      onAuthClick?.();
    }
  };

  const onGetStartedClick = () => {
    if (user) {
      navigate("/virtual-binder");
    } else {
      onAuthClick?.();
    }
  };

  const glowColors = [
    "rgba(250, 204, 21, 0.7)", // electric-yellow
    "rgba(0, 194, 255, 0.7)", // holo-blue
    "rgba(233, 52, 69, 0.7)", // poke-red
    "rgba(255, 20, 147, 0.7)", // fluorescent-pink
    "rgba(16, 185, 129, 0.7)", // green
  ];

  // Change color every 10 minutes (600,000ms)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentColor((prev) => (prev + 1) % glowColors.length);
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  // Also change color when component mounts (page change)
  useEffect(() => {
    setCurrentColor((prev) => (prev + 1) % glowColors.length);
  }, []);

  return (
    <section className="bg-gradient-to-br from-llama-white via-holo-blue/5 to-electric-yellow/10 dark:from-background dark:via-holo-blue/10 dark:to-electric-yellow/5 py-12 sm:py-16 lg:py-20 relative overflow-hidden">
      {/* Background elements - responsive positioning */}
      <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-24 h-24 sm:w-32 sm:h-32 bg-electric-yellow/10 dark:bg-electric-yellow/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-32 h-32 sm:w-40 sm:h-40 bg-holo-blue/10 dark:bg-holo-blue/20 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative">
        <div className="text-center max-w-5xl mx-auto">
          {/* Brand Logo with dynamic color pulsing - responsive sizing */}
          <div className="mb-6 sm:mb-8 flex justify-center">
            <div
              className=" rounded-full flex items-center justify-center relative"
              style={
                {
                  animation: "logo-pulse 2s ease-in-out infinite",
                  "--pulse-color": glowColors[currentColor],
                } as React.CSSProperties & { "--pulse-color": string }
              }
            >
              <img
                src="/pokellama_logo.webp"
                alt="logo"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full"
              />
              <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-3 h-3 sm:w-4 sm:h-4 bg-electric-yellow rounded-full border-2 border-deep-indigo"></div>
            </div>
          </div>

          {/* Headline - responsive typography */}
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold font-poppins mb-4 sm:mb-6 bg-gradient-to-r from-deep-indigo via-holo-blue to-poke-red bg-clip-text text-transparent dark:text-fluorescent-blue leading-tight px-2">
            The Ultimate Pokémon Card Value Tracker
          </h1>

          {/* Subtext - Enhanced for better visibility and responsive */}
          <p className="text-lg sm:text-xl lg:text-2xl text-deep-indigo dark:text-white mb-6 sm:mb-8 font-inter font-semibold bg-white/60 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl px-4 sm:px-8 py-3 sm:py-4 shadow-lg border border-white/20 dark:border-slate-700/50 mx-4 sm:mx-0">
            Track your collection. Explore trends.{" "}
            <span className="text-holo-blue dark:text-electric-yellow font-bold">
              Flex your binder.
            </span>
          </p>

          {/* CTA Button - responsive sizing */}
          <div className="mb-12 sm:mb-16">
            <Button
              onClick={onGetStartedClick}
              size="lg"
              className="bg-gradient-to-r from-electric-yellow to-holo-blue hover:from-electric-yellow/90 hover:to-holo-blue/90 text-deep-indigo dark:text-background font-bold font-inter text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Get Started
            </Button>
          </div>

          {/* Feature highlights - responsive grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 px-4 sm:px-0">
            <div className="bg-white/60 dark:bg-card/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-holo-blue/20 dark:border-holo-blue/40 hover:border-holo-blue/40 dark:hover:border-holo-blue/60 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-holo-blue to-deep-indigo rounded-xl flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-poppins mb-2 text-deep-indigo dark:text-white bg-transparent px-3 sm:px-4 py-2 rounded-lg">
                Real-time card price tracking
              </h3>
              <p className="text-sm sm:text-base text-deep-indigo/70 dark:text-muted-foreground font-inter">
                Live market data and price movements for every card in your
                collection.
              </p>
            </div>

            <div
              onClick={handleBinderCardClick}
              className="bg-white/60 dark:bg-card/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-electric-yellow/20 dark:border-electric-yellow/40 hover:border-electric-yellow/40 dark:hover:border-electric-yellow/60 transition-all duration-300 hover:shadow-lg cursor-pointer transform hover:scale-105"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-electric-yellow to-poke-red rounded-xl flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                <Star className="w-5 h-5 sm:w-6 sm:h-6 text-deep-indigo" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-poppins mb-2 text-deep-indigo dark:text-white bg-transparent px-3 sm:px-4 py-2 rounded-lg">
                Build your virtual binder
              </h3>
              <p className="text-sm sm:text-base text-deep-indigo/70 dark:text-muted-foreground font-inter">
                Organize and showcase your collection with our beautiful digital
                binder.
              </p>
            </div>

            <div className="bg-white/60 dark:bg-card/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-poke-red/20 dark:border-poke-red/40 hover:border-poke-red/40 dark:hover:border-poke-red/60 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-poke-red to-deep-indigo rounded-xl flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-poppins mb-2 text-deep-indigo dark:text-white bg-transparent px-3 sm:px-4 py-2 rounded-lg">
                See your PokéScore power rating
              </h3>
              <p className="text-sm sm:text-base text-deep-indigo/70 dark:text-muted-foreground font-inter">
                Gamified collection scoring and leaderboard rankings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
